/**
 * ThemeContext - Provides dynamic theming with CSS variables
 *
 * Uses tenant theme to apply custom colors throughout the app.
 * Integrates with TailwindCSS via CSS custom properties.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { TenantTheme, DEFAULT_THEME } from '@/types/tenant.types';

// =============================================================================
// Types
// =============================================================================

interface ThemeContextValue {
  theme: TenantTheme;
  setTheme: (theme: Partial<TenantTheme>) => void;
  resetTheme: () => void;
  isDark: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// =============================================================================
// CSS Variable Application
// =============================================================================

/**
 * Convert hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Apply theme to CSS custom properties
 */
function applyThemeToCSS(theme: TenantTheme) {
  const root = document.documentElement;

  // Primary color
  root.style.setProperty('--color-primary', theme.primaryColor);
  const primaryRgb = hexToRgb(theme.primaryColor);
  if (primaryRgb) {
    root.style.setProperty(
      '--color-primary-rgb',
      `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`
    );
  }

  // Secondary color
  root.style.setProperty('--color-secondary', theme.secondaryColor);
  const secondaryRgb = hexToRgb(theme.secondaryColor);
  if (secondaryRgb) {
    root.style.setProperty(
      '--color-secondary-rgb',
      `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`
    );
  }

  // Accent color (opcional)
  if (theme.accentColor) {
    root.style.setProperty('--color-accent', theme.accentColor);
    const accentRgb = hexToRgb(theme.accentColor);
    if (accentRgb) {
      root.style.setProperty(
        '--color-accent-rgb',
        `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`
      );
    }
  } else {
    root.style.removeProperty('--color-accent');
    root.style.removeProperty('--color-accent-rgb');
  }

  // Font family (legacy + novos display/body)
  const fontBody = theme.fontBody || theme.fontFamily;
  const fontDisplay = theme.fontDisplay || fontBody;

  if (fontBody) {
    root.style.setProperty('--font-family', fontBody);
    root.style.setProperty('--font-body', fontBody);
  }
  if (fontDisplay) {
    root.style.setProperty('--font-display', fontDisplay);
  }

  // Logo URL (as CSS variable for backgrounds)
  if (theme.logoUrl) {
    root.style.setProperty('--logo-url', `url(${theme.logoUrl})`);
  }
}

/**
 * Update document favicon
 */
function updateFavicon(faviconUrl: string) {
  if (!faviconUrl) return;

  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}

// =============================================================================
// Provider Component
// =============================================================================

interface ThemeProviderProps {
  children: React.ReactNode;
  initialTheme?: Partial<TenantTheme>;
}

export function ThemeProvider({ children, initialTheme }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<TenantTheme>({
    ...DEFAULT_THEME,
    ...initialTheme,
  });

  const [isDark, setIsDark] = useState(() => {
    // Check for saved preference or system preference
    const saved = localStorage.getItem('campanha-flow-dark-mode');
    if (saved !== null) {
      return saved === 'true';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply theme to CSS when it changes
  useEffect(() => {
    applyThemeToCSS(theme);
    updateFavicon(theme.faviconUrl);
  }, [theme]);

  // Apply dark mode class
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('campanha-flow-dark-mode', String(isDark));
  }, [isDark]);

  const setTheme = (updates: Partial<TenantTheme>) => {
    setThemeState((prev) => ({ ...prev, ...updates }));
  };

  const resetTheme = () => {
    setThemeState(DEFAULT_THEME);
  };

  const toggleDarkMode = () => {
    setIsDark((prev) => !prev);
  };

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      resetTheme,
      isDark,
      toggleDarkMode,
    }),
    [theme, isDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

// =============================================================================
// CSS for Tailwind Integration
// =============================================================================

/**
 * Add this to your global CSS file (index.css):
 *
 * :root {
 *   --color-primary: #2563eb;
 *   --color-primary-rgb: 37, 99, 235;
 *   --color-secondary: #1e40af;
 *   --color-secondary-rgb: 30, 64, 175;
 *   --font-family: 'Inter', system-ui, sans-serif;
 * }
 *
 * Then in tailwind.config.js extend colors:
 *
 * theme: {
 *   extend: {
 *     colors: {
 *       primary: {
 *         DEFAULT: 'var(--color-primary)',
 *         rgb: 'rgb(var(--color-primary-rgb))',
 *       },
 *       secondary: {
 *         DEFAULT: 'var(--color-secondary)',
 *         rgb: 'rgb(var(--color-secondary-rgb))',
 *       },
 *     },
 *     fontFamily: {
 *       sans: ['var(--font-family)'],
 *     },
 *   },
 * }
 *
 * Usage in components:
 * <button className="bg-primary text-white hover:bg-primary/90">
 *   Click me
 * </button>
 */
